/**
 * The on-disk copy of the per-address overlay cache.
 *
 * `addressOverlayRefCache` caches one answer per `(chain, address)` in memory,
 * so a page asks about an address once while it is open. Close the tab, reload,
 * or come back after the in-memory copy was collected, and every address on an
 * investigation's table is asked about again — a case can hold a thousand. This
 * keeps the answers in IndexedDB, under the same identity prefix every other
 * piece of per-user browser data uses, so a revisit starts from them.
 *
 * What makes a catalog answer harder to keep than a transaction body is that it
 * is NOT content-addressed. A body is correct forever under its etag; an
 * overlay is the precedence engine's answer at a moment, and anyone on any rung
 * can change it — an operator's global row, an org admin, a teammate. It also
 * carries accusations: a `sanctioned` tag attached while the tab was closed must
 * not be hidden behind a "not catalogued" kept from before. So a kept answer is
 * a HINT with three rules:
 *
 * - **Trusted only briefly.** Inside {@link OVERLAY_DISK_TRUST_MS} of when the
 *   server gave it, it is served without asking. That window is the bound on
 *   how stale an answer can be when a change happened while nothing was
 *   listening (the tab was closed).
 * - **Never trusted across a change this browser heard of.** Every catalog
 *   create/update/delete the resource-change hub delivers — this tab's own
 *   writes, another tab's, a stream event — stamps {@link markCatalogChanged},
 *   in localStorage so every tab sees it, and an answer from before that stamp
 *   is not trusted however young it is. That is the "I just tagged it and the
 *   table still says clean" case, which a window alone would reopen.
 * - **Otherwise shown while it is re-checked.** Past the window (up to
 *   {@link OVERLAY_DISK_MAX_AGE_MS}) the kept answer paints at once and the
 *   request goes out anyway; the server's answer replaces it.
 *
 * Only answers the server vouched for are kept — `status: "resolved"`. A lookup
 * that failed server-side degrades to the same empty block as a clean address,
 * and keeping that would keep "could not check" as "clean" for a day. See
 * `isOverlayAnswerConfirmed` in fe-commons.
 *
 * Identity: keys carry the full `customer/org/tenant/user` quadruple, exactly as
 * `ScopedTxCache` does, and the store is erased with the rest of an identity's
 * browser data (`@/app/browserData`). An anonymous page keeps nothing.
 */
import {
  ScopedStorage,
  createIdbKvStore,
  type KvStore,
  type StorageIdentity,
  type StorageLevel,
} from "@authe/fe-commons/storage";
import { currentIdentity } from "@/app/txCache";
import type { AddressOverlayRefEntry } from "./addressOverlayRefCache";

/** How long after the server answered a kept answer is served without asking. */
export const OVERLAY_DISK_TRUST_MS = 10 * 60 * 1000;

/**
 * How long a kept answer may stay on disk at all — and so how old an answer may
 * be painted while it is re-checked. A day covers "reopen the case tomorrow";
 * past it, a blank row that fills in is better than a day-old accusation.
 */
export const OVERLAY_DISK_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Kept answers per identity; the oldest go first when {@link AddressOverlayDiskCache.prune} runs. */
export const OVERLAY_DISK_MAX_ENTRIES = 20_000;

/** Bumped when the kept shape changes, so an old shape is a miss rather than a misread. */
const KEY_PREFIX = "overlay-ref:v1:";

/** The localStorage name of the "a catalog change was heard" stamp. */
const CHANGED_AT_NAME = "overlay-ref.catalog-changed-at";

/** Its own database, so its lifecycle cannot touch the transaction body cache. */
const DB_NAME = "authe-overlays";

interface KeptAnswer {
  /** When the request that produced it was SENT — not when it came back. */
  asOf: number;
  entry: AddressOverlayRefEntry;
}

/** A kept answer, and whether it may be served without asking. */
export interface OverlayDiskHit {
  entry: AddressOverlayRefEntry;
  asOf: number;
  trusted: boolean;
}

export interface AddressOverlayDiskCacheOptions {
  trustMs?: number;
  maxAgeMs?: number;
  maxEntries?: number;
  now?: () => number;
}

function isKeptAnswer(value: unknown): value is KeptAnswer {
  if (!value || typeof value !== "object") return false;
  const v = value as Partial<KeptAnswer>;
  return (
    typeof v.asOf === "number" &&
    !!v.entry &&
    typeof v.entry.address === "string" &&
    v.entry.status === "resolved"
  );
}

export class AddressOverlayDiskCache {
  private readonly scope: ScopedStorage;
  private readonly trustMs: number;
  private readonly maxAgeMs: number;
  private readonly maxEntries: number;
  private readonly now: () => number;

  constructor(
    private readonly store: KvStore,
    identity: StorageIdentity,
    options: AddressOverlayDiskCacheOptions = {},
  ) {
    this.scope = new ScopedStorage(identity);
    this.trustMs = options.trustMs ?? OVERLAY_DISK_TRUST_MS;
    this.maxAgeMs = options.maxAgeMs ?? OVERLAY_DISK_MAX_AGE_MS;
    this.maxEntries = options.maxEntries ?? OVERLAY_DISK_MAX_ENTRIES;
    this.now = options.now ?? Date.now;
  }

  /** Nothing is kept for, or read by, a page with no signed-in user. */
  get enabled(): boolean {
    return !!this.scope.identity.userId;
  }

  /**
   * The disk key for one per-ref query key. Everything after the family name is
   * in it — the ref, `includeRow`, the tier, `withScopes` — because each changes
   * what the answer contains, exactly as it does in memory.
   */
  keyFor(queryKey: readonly unknown[]): string {
    return this.scope.keyAt(`${KEY_PREFIX}${JSON.stringify(queryKey.slice(1))}`, "user");
  }

  /** When this identity last heard of a catalog change (0 when never). */
  changedAt(): number {
    const value = this.scope.getAt<unknown>(CHANGED_AT_NAME, "user", 0);
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
  }

  /** Stop trusting every answer kept before now, in every tab. */
  markCatalogChanged(): void {
    if (!this.enabled) return;
    this.scope.set(CHANGED_AT_NAME, this.now(), "user");
  }

  /** The kept answer for this query, if any. Never throws. */
  async read(queryKey: readonly unknown[]): Promise<OverlayDiskHit | undefined> {
    if (!this.enabled) return undefined;
    try {
      const kept = await this.store.get<unknown>(this.keyFor(queryKey), { maxAgeMs: this.maxAgeMs });
      if (!isKeptAnswer(kept)) return undefined;
      const now = this.now();
      if (now - kept.asOf > this.maxAgeMs) return undefined;
      // Strictly after: an answer requested in the same millisecond as a change
      // cannot be shown to have seen it.
      const trusted = now - kept.asOf <= this.trustMs && kept.asOf > this.changedAt();
      return { entry: kept.entry, asOf: kept.asOf, trusted };
    } catch {
      return undefined;
    }
  }

  /**
   * Keep a server answer. `asOf` is when its request was SENT, so a request in
   * flight across a catalog change keeps an answer the change stamp outranks.
   * Anything but a confirmed answer is refused. Never throws.
   */
  async write(queryKey: readonly unknown[], entry: AddressOverlayRefEntry, asOf: number): Promise<void> {
    if (!this.enabled || entry.status !== "resolved") return;
    try {
      await this.store.set<KeptAnswer>(this.keyFor(queryKey), { asOf, entry });
    } catch {
      /* Best-effort: the next page asks the server, as it always did. */
    }
  }

  /** Drop every kept answer at `level` and narrower for this identity. */
  clear(level: StorageLevel): Promise<number> {
    return this.store.clearPrefix(this.prefixAt(level));
  }

  /** Drop every kept answer for every identity — what a sign-out wants. */
  clearAll(): Promise<number> {
    return this.store.sweep(() => false);
  }

  /**
   * Keep only this identity's answers younger than the max age, and at most
   * {@link OVERLAY_DISK_MAX_ENTRIES} of those (the newest). Run once a session
   * is established, for what no sign-out erased.
   */
  async prune(): Promise<{ removed: number }> {
    if (!this.enabled) return { removed: 0 };
    const now = this.now();
    const own = this.prefixAt("user");
    const kept: number[] = [];
    let removed = await this.store.sweep((key, writtenAt) => {
      const keep = key.startsWith(own) && writtenAt > 0 && now - writtenAt <= this.maxAgeMs;
      if (keep) kept.push(writtenAt);
      return keep;
    });
    if (kept.length > this.maxEntries) {
      kept.sort((a, b) => b - a);
      const cutoff = kept[this.maxEntries - 1]!;
      removed += await this.store.sweep((_key, writtenAt) => writtenAt >= cutoff);
    }
    return { removed };
  }

  private prefixAt(level: StorageLevel): string {
    return `${this.scope.keyAt("", level).replace(/:$/, "")}:`;
  }
}

let sharedStore: KvStore | null = null;

/** One IndexedDB handle for the page, opened on first use. */
function overlayDiskStore(): KvStore {
  if (!sharedStore) sharedStore = createIdbKvStore({ dbName: DB_NAME });
  return sharedStore;
}

/**
 * The disk cache for whoever is signed in now, or `null` for an anonymous page.
 *
 * Resolved per call from the live routing token, like `txCache()`: a cache
 * pinned to the identity current at module load would keep one tenant's answers
 * under another's prefix after a switch.
 *
 * Never throws. It runs inside every overlay query, and the disk is an
 * optimisation: an identity that cannot be read (a token mid-refresh, a config
 * that is not there) must leave the page reading the server as it always did,
 * not turn every address on it into a failed query.
 */
export function overlayDiskCache(): AddressOverlayDiskCache | null {
  try {
    const cache = new AddressOverlayDiskCache(overlayDiskStore(), currentIdentity());
    return cache.enabled ? cache : null;
  } catch {
    return null;
  }
}

/**
 * The page's store, for the identity lifecycle (`@/app/browserData`) — which
 * must reach it even when no user is signed in.
 */
export function overlayDiskStoreForCleanup(): KvStore {
  return overlayDiskStore();
}

/**
 * Stop trusting kept answers whenever the catalog changes under this identity.
 *
 * Wired to the resource-change hub's IMMEDIATE listener, so the stamp is down
 * before the batched invalidation refetches anything. Only real edits count:
 * `revalidate()` also delivers an `invalidated` for every resource on every
 * focus, visibility change and stream (re)connect — "something may have been
 * missed", which is exactly what the trust window already bounds. Counting those
 * would distrust the whole disk a second after every page load.
 */
export function watchCatalogChangesForOverlayDisk(
  hub: { subscribe: (listener: (change: { resource: string; operation: string }) => void) => () => void },
  dependsOn: (resource: string) => boolean,
): () => void {
  return hub.subscribe(({ resource, operation }) => {
    if (operation === "invalidated" || !dependsOn(resource)) return;
    overlayDiskCache()?.markCatalogChanged();
  });
}

/** Test seam: back the page's disk cache with `store` (`null` restores IndexedDB). */
export function __setOverlayDiskStore(store: KvStore | null): void {
  sharedStore = store;
}
